import { useNavigate } from 'react-router-dom';
import Avatar from '@/components/base/Avatar';
import Badge from '@/components/base/Badge';
import StatusBadge from '@/components/base/StatusBadge';
import { formatMoney, formatDate } from '@/utils/format';
import { summarizeChecks } from '@/utils/invoice';
import EarlyPayoutNotice from '@/components/feature/EarlyPayoutNotice';
import type { Invoice } from '@/types/portal';

interface InvoiceTableProps {
  rows: Invoice[];
  selected: string[];
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  statusOf: (invoice: Invoice) => string;
}

function CheckButton({
  checked,
  onToggle,
  label,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      className={`flex h-5 w-5 items-center justify-center rounded border text-[13px] cursor-pointer transition-colors ${
        checked
          ? 'border-primary-500 bg-primary-500 text-background-50'
          : 'border-background-300 bg-background-50 text-transparent hover:border-primary-400'
      }`}
    >
      <i className="ri-check-line" />
    </button>
  );
}

export default function InvoiceTable({
  rows,
  selected,
  onToggle,
  onToggleAll,
  statusOf,
}: InvoiceTableProps) {
  const navigate = useNavigate();

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
        <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-xl">
          <i className="ri-file-list-3-line" />
        </span>
        <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
          No invoices match
        </h3>
        <p className="mt-1 max-w-sm text-sm text-foreground-600">
          Try another status tab or clear the market, early payout and search filters.
        </p>
      </div>
    );
  }

  const allSelected = rows.every((row) => selected.includes(row.id));

  return (
    <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
      <table className="row-accent w-full min-w-[980px] border-collapse text-left">
        <thead>
          <tr className="border-b border-background-200 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            <th className="w-12 px-5 py-3">
              <CheckButton
                checked={allSelected}
                onToggle={onToggleAll}
                label="Select all invoices"
              />
            </th>
            <th className="px-3 py-3 font-semibold">Invoice</th>
            <th className="px-3 py-3 font-semibold">Creator</th>
            <th className="px-3 py-3 font-semibold">Campaign</th>
            <th className="px-3 py-3 font-semibold">Amount</th>
            <th className="px-3 py-3 font-semibold">Status</th>
            <th className="px-3 py-3 font-semibold">Due</th>
            <th className="px-5 py-3 text-right font-semibold">Checks</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((invoice) => {
            const status = statusOf(invoice);
            const summary = summarizeChecks(invoice);
            return (
              <tr
                key={invoice.id}
                onClick={() => navigate(`/invoices/${invoice.id}`)}
                className={`cursor-pointer border-b border-background-100 last:border-0 hover:bg-background-100 ${
                  selected.includes(invoice.id) ? 'bg-primary-50/60' : ''
                }`}
              >
                <td className="px-5 py-3.5">
                  <CheckButton
                    checked={selected.includes(invoice.id)}
                    onToggle={() => onToggle(invoice.id)}
                    label={`Select ${invoice.number}`}
                  />
                </td>
                <td className="px-3 py-3.5">
                  <p className="text-sm font-medium text-foreground-950">{invoice.number}</p>
                  <p className="text-xs text-foreground-500">{formatDate(invoice.issuedAt)}</p>
                </td>
                <td className="px-3 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <Avatar src={invoice.avatar} alt={invoice.creatorName} size="sm" />
                    <div className="min-w-0">
                      <p className="text-sm text-foreground-900">{invoice.creatorName}</p>
                      <p className="text-xs text-foreground-500">{invoice.market}</p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <p className="max-w-[190px] truncate text-sm text-foreground-700">
                    {invoice.campaign}
                  </p>
                  <p className="max-w-[190px] truncate text-xs text-foreground-400">
                    {invoice.deliverableRef}
                  </p>
                </td>
                <td className="px-3 py-3.5">
                  <p className="text-sm font-medium text-foreground-950">
                    {formatMoney(invoice.amount, invoice.currency)}
                  </p>
                  <p className="text-xs text-foreground-400">{invoice.rail}</p>
                  {invoice.advance ? (
                    <span className="mt-1 flex">
                      <EarlyPayoutNotice
                        variant="inline"
                        advance={invoice.advance}
                        currency={invoice.currency}
                        invoiceTotal={invoice.amount}
                      />
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-3.5">
                  <StatusBadge status={status} />
                </td>
                <td className="px-3 py-3.5 text-xs text-foreground-600">
                  {formatDate(invoice.dueAt)}
                  {invoice.flags.length > 0 ? (
                    <span className="mt-1 flex">
                      <Badge tone="primary" icon="ri-flag-line" className={invoice.flags[0] === 'Overdue' ? 'flag-pulse' : ''}>
                        {invoice.flags[0]}
                      </Badge>
                    </span>
                  ) : null}
                </td>
                <td className="px-5 py-3.5 text-right">
                  <span className="inline-flex justify-end">
                    <Badge
                      tone={summary.tone === 'primary' ? 'primary' : summary.tone === 'secondary' ? 'secondary' : 'accent'}
                      icon={
                        summary.tone === 'primary'
                          ? 'ri-error-warning-line'
                          : summary.tone === 'secondary'
                            ? 'ri-alert-line'
                            : 'ri-check-line'
                      }
                    >
                      {summary.label}
                    </Badge>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}